import * as Sentry from '@sentry/react'
import { setProvideFeedbackForm } from 'app/redux/actions/FormsActions'
import { ReduxState } from 'app/redux/ReduxInterfaces'
import routes from 'app/RootRoutes'
import {
  checkIfOrganizationsAreStale,
  getUsersOrganizationsJoinRequests
} from 'app/services/sfAuth/sfData/sfAccount'
import { enforceQuerySuccess } from 'app/services/sfAuth/sfData/sfCommon'
import { getContactsMapByFlow } from 'app/services/sfAuth/sfData/sfContact'
import { getFormPage } from 'app/services/sfAuth/sfData/sfForms'
import { getExternalReviewer } from 'app/services/sfAuth/sfData/sfReviews'
import { checkUserProfileValidity } from 'app/services/sfAuth/sfData/sfUser'
import { NOT_FOUND_PATH } from 'app/views/sessions/SessionRoutes'
import Loading from 'egret/components/EgretLoadable/Loading'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { valueExistsTS } from 'utilsTS'
import { setConfigurationData } from '../redux/actions/ConfigurationActions'
import { userLoggedIn, userLoggedOut } from '../redux/actions/LoginActions'
import { setOrganization } from '../redux/actions/OrganizationActions'
import { setUserData } from '../redux/actions/UserActions'
import localStorageService from '../services/localStorageService'
import sfOauthConfig from '../services/sfAuth/sfAuthConfig'
import sfAuthService from '../services/sfAuth/SFAuthService'
import { checkAuth } from './authRoles'

/**
 * handle login and authorization for SF
 * @component
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const Auth = ({ children }): JSX.Element => {
  const [loading, setLoading] = useState(true)
  const [authenticated, setAuthenticated] = useState(true)
  const { user, organization, login } = useSelector<ReduxState, Partial<ReduxState>>(state => ({
    user: state.user,
    organization: state.organization,
    login: state.login
  }))

  const isInternal: boolean = sfOauthConfig.isInternal

  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const { pathname } = location

  const handleRefresh = (): void => {
    window.location.reload()
  }

  const redirectToNotFOund = (): void => {
    history.push({
      pathname: NOT_FOUND_PATH,
      state: { redirectUrl: pathname }
    })
  }

  useEffect(() => {
    const user = localStorageService.getItem('auth_user')
    dispatch(setUserData(user))
    checkSfAuth(user)
  }, [])

  useEffect(() => {
    if (user != null) {
      const matched = routes.find(r => r.path === pathname)
      if (matched == null) {
        return
      }
      const authenticated = valueExistsTS(matched?.auth)
        ? checkAuth(matched.auth, user.role, matched, { user, organization })
        : true
      if (!authenticated && 'access_token' in user) {
        redirectToNotFOund()
      }
      setAuthenticated(authenticated)
    }
  }, [pathname, user])

  const handleDataFetchSuccess = ({ user, organization, configuration, state }): void => {
    // Set feedback forms ids
    enforceQuerySuccess(async () =>
      await Promise.all([
        getFormPage(configuration.FORM_FEEDBACK),
        getFormPage(configuration.FORM_RESOURCE_FEEDBACK)
      ]).then(([provideFeedback, resourceFeedbackForm]) => {
        dispatch(setProvideFeedbackForm({
          provideFeedback,
          resourceFeedbackForm
        }))
      })
    ).catch(error => {
      console.error(error)
    })

    if (!isInternal) {
      enforceQuerySuccess(async () =>
        await getUsersOrganizationsJoinRequests(user.userId)
      ).then(requests => {
        dispatch(
          setUserData({ organizationsPendingRequests: requests.length })
        )
      }).catch(error => {
        console.error(error)
      })

      enforceQuerySuccess(async () =>
        await checkUserProfileValidity(user.userId, pathname).then(
          isValid => {
            dispatch(
              setUserData({ isUserProfileValid: Boolean(isValid) })
            )
          }
        )
      ).catch(error => {
        console.error(error)
        Sentry.withScope((scope) => {
          const isErrorObject = e instanceof Error || (e && typeof e === 'object' && e.message)

          scope.setTag('eventType', 'login_failure')
          scope.setTag('errorType', isErrorObject ? (e.name || 'Error') : e)
          scope.setLevel('error')

          scope.setContext('Login attempt', {
            'Error type': isErrorObject ? (e.name || 'Error') : e,
            'Error message': isErrorObject ? e.message : e,
            'Error stack': isErrorObject ? e.stack : null
          })

          if (isErrorObject) {
            Sentry.captureException(e)
          } else {
            Sentry.captureMessage(`Login failed: ${e}`, 'error')
          }
        })
      })
    }

    enforceQuerySuccess(async () =>
      await checkIfOrganizationsAreStale(user.userId)
    ).then(staleOrganizations => {
      dispatch(setUserData({ staleOrganizations }))
    }).catch(error => {
      console.error(error)
    })

    // enforceQuerySuccess(() => getTutorialStepsParsed()).then(
    //   tutorialSteps => {
    //     dispatch(setTutorialsFromSteps(tutorialSteps))
    //   }
    // ).catch(error => {
    //   console.error(error)
    // })

    dispatch(setOrganization(organization, user))
    dispatch(setConfigurationData(configuration))
    dispatch(userLoggedIn())

    setLoading(false)

    getExternalReviewer(user.userObject.contactId).then(result => {
      if (result[0] != null) {
        dispatch(
          setUserData({ ...user, hasExternalReviewProfile: true })
        )
      }
    }).catch(error => {
      console.error(error)
    })

    getContactsMapByFlow([user.userObject.contactId]).then(result => {
      const contact = Object.values(result)?.[0]
      if (contact != null) {
        dispatch(setUserData({
          ...user,
          contact: {
            id: contact.Id,
            whatAreYouLookingFor: contact.What_are_you_looking_for__c
          }
        }))
      } else {
        dispatch(setUserData({ ...user, contact: null }))
      }
    }).catch(error => {
      console.error(error)
    })

    try {
      history.push({
        pathname: state.redirect_uri
      })
    } catch (e) {
      console.error("can't redirect going to root", e, user, state)
      // window.location = '/'
    }
  }

  const checkSfAuth = (user): void => {
    const sentState = {
      redirect_uri: location.pathname
    }
    if (login?.success) {
      console.log('user logged in')
      if (sfAuthService?.user?.access_token == null) {
        sfAuthService.setUser(user)
      }
      setLoading(false)
    } else if (user?.access_token && user?.userId) {
      console.log('got existing user session', user, organization)
      sfAuthService.setUser(user)
      sfAuthService
        .checkLogin(user)
        .then(
          ({
            user,
            state,
            organization,
            configuration
          }) => {
            handleDataFetchSuccess({
              user,
              state,
              organization,
              configuration
            })
            sfAuthService.setUser(user)
          },
          e => {
            console.error('user check failed', e, user)
            sfAuthService.setUser(null)
            dispatch(userLoggedOut())
            sfAuthService.doLogin(sentState)
          }
        )
        .catch(e => {
          console.error('cant login from stored user', e, login)
          sfAuthService.setUser(null)
          dispatch(userLoggedOut())
          sfAuthService.doLogin(sentState)
        })
    } else {
      sfAuthService
        .parseToken()
        .then(
          ({
            user,
            state,
            organization,
            configuration
          }) => {
            handleDataFetchSuccess({
              user,
              state,
              organization,
              configuration
            })
          }
        )
        .catch(e => {
          console.error('cant login', e, login)
          // if (e.message === 'Network Error') {
          //   return setNetworkError(e.message)
          // }
          sfAuthService.doLogin(sentState)
        })
    }
  }

  if (loading || !authenticated || user?.access_token == null) {
    return <Loading />
  }

  return (
    <>
      {/* <MaintenanceWarningDialog
        dialogStartDate={maintenance?.maintenanceDialogStartDate}
        maintenanceStartDate={maintenance?.startDate}
        maintenanceEndDate={maintenance?.endDate}
        clousureStartDate={maintenance?.clousureStartDate}
      /> */}
      {children}
    </>
  )
}

export default Auth
